'use client'

import {useEffect, useMemo, useState} from 'react'

import {closeNotif, startNotif} from '@/components/DynamicForm/util/notifier'
import Splash from '@/components/splash/Splash'
import {Button, Checkbox} from '@/components/UiElements'
import getCompanyRoles from '@/services/UserService/getCompanyRoles'
import updateUserRolesV2 from '@/services/UserService/updateUserRolesV2'
import {Role} from '@/types/role'
import {User} from '@/types/user'
import {getErrorMessage} from '@/utils/errorMessages'
import notifyError from '@/utils/notifyError'

type AssignRolesModalProps = {
    user: User
    assignedRoles: Role[]
    onClose: () => void
    onRolesAssigned: (roles: Role[]) => void
}

const getRoleKey = (role: Role) => role.id || role.name

const AssignRolesModal = ({
    user,
    assignedRoles,
    onClose,
    onRolesAssigned,
}: AssignRolesModalProps) => {
    const [roles, setRoles] = useState<Role[]>([])
    const [selectedRoleKeys, setSelectedRoleKeys] = useState<Set<string>>(new Set())
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)

    const assignedRoleKeys = useMemo(
        () => new Set(assignedRoles.flatMap(role => [role.id, role.name]).filter(Boolean)),
        [assignedRoles],
    )

    useEffect(() => {
        let cancelled = false

        const loadRoles = async () => {
            setIsLoading(true)

            try {
                const result = await getCompanyRoles()
                if (cancelled) return

                setRoles(Array.isArray(result) ? result : result?.roles || [])
            } catch (error) {
                if (!cancelled) {
                    setRoles([])
                    notifyError(error, 'Unable to load roles.')
                }
            } finally {
                if (!cancelled) setIsLoading(false)
            }
        }

        void loadRoles()

        return () => {
            cancelled = true
        }
    }, [])

    const selectedRoles = useMemo(
        () => roles.filter(role => selectedRoleKeys.has(getRoleKey(role))),
        [roles, selectedRoleKeys],
    )

    const handleRoleChange = (role: Role, checked: boolean) => {
        const roleKey = getRoleKey(role)

        setSelectedRoleKeys(current => {
            const next = new Set(current)
            if (checked) {
                next.add(roleKey)
            } else {
                next.delete(roleKey)
            }
            return next
        })
    }

    const handleAssignRoles = async () => {
        if (selectedRoles.length === 0 || isSaving) return

        setIsSaving(true)
        const notificationId = startNotif(
            selectedRoles.length === 1 ? 'Assigning role...' : 'Assigning roles...',
        )

        try {
            await updateUserRolesV2({
                auth0_user_id: user.email,
                roles: selectedRoles.map(role => role.name),
            })
            closeNotif(
                notificationId,
                selectedRoles.length === 1
                    ? 'Role assigned successfully.'
                    : 'Roles assigned successfully.',
            )
            onRolesAssigned(selectedRoles)
            onClose()
        } catch (error) {
            closeNotif(
                notificationId,
                getErrorMessage(error, 'Failed to assign roles.'),
                {type: 'error'},
            )
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="flex w-[560px] max-w-[calc(100vw-80px)] flex-col gap-xl">
            {isLoading && <Splash text="Loading roles..." />}

            {!isLoading && roles.length > 0 && (
                <ul className="divide-y divide-gray-200 border-y border-gray-200">
                    {roles.map(role => {
                        const isAssigned = assignedRoleKeys.has(role.id) || assignedRoleKeys.has(role.name)

                        return (
                            <li key={getRoleKey(role)} className="px-md">
                                <Checkbox
                                    label={role.name}
                                    description={role.description}
                                    defaultChecked={isAssigned || selectedRoleKeys.has(getRoleKey(role))}
                                    disabled={isAssigned || isSaving}
                                    onChange={checked => handleRoleChange(role, checked)}
                                    className={() => 'min-h-[56px] w-full py-sm'}
                                />
                            </li>
                        )
                    })}
                </ul>
            )}

            {!isLoading && roles.length === 0 && (
                <div className="flex min-h-[100px] items-center justify-center rounded-md bg-gray-50 p-xl text-text-md text-gray-400">
                    No roles are available.
                </div>
            )}

            <div className="flex justify-end gap-md">
                <Button size="lg" type="alt" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    size="lg"
                    onClick={handleAssignRoles}
                    disabled={isLoading || isSaving || selectedRoles.length === 0}
                >
                    Assign
                </Button>
            </div>
        </div>
    )
}

export default AssignRolesModal
